import { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/ui-kit/molecules/modal';
import { Button } from '@/ui-kit/atoms/button';
import { RequiredMark } from '@/ui-kit/atoms/required-mark';
import { apiJson, type HttpError } from '@/app-kit/request-service';
import type { EditableOrderLine } from '@/app-kit/use-orders';
import { Th, Td, decimal2 } from './order-payouts-table';
import {
  ORDER_LINE_EDITABLE_FIELDS,
  ORDER_LINE_FIELD_LABELS,
  buildLinesPatch,
  initialDrafts,
  parseDraft,
  previewLine,
  previewTotals,
  type LineDrafts,
  type OrderLineEditableField,
  type OrderLinePatch,
} from './order-lines-edit';

/**
 * Edit Qty / PPV Each / CV Each on an order's lines.
 *
 * One table, every line at once, with the line's PPV/CV and the order's new
 * totals previewed as the operator types. Saves through
 * `PATCH /api/customers/:cid/orders/:oid/lines`, which recomputes the line
 * volumes and re-sums the order totals server-side.
 *
 * Locked on the ORDER's dx_version (order_details has none of its own — see
 * services/order-line-edit.ts). On a 409 the modal shows which line values
 * another editor changed, Was/Now, and offers Reload — the same contract as
 * EditFieldGroupModal.
 */

type ConflictBody = {
  error:            string;
  current_row?:     { line_items?: EditableOrderLine[] };
  current_version?: number;
};

type Props = {
  open:       boolean;
  onClose:    () => void;
  customerId: number;
  orderId:    number;
  /**
   * The ORDER's dx_version, as read with these lines. Both are pinned when the
   * modal mounts — see the note in the body.
   */
  dxVersion:  number;
  lines:      EditableOrderLine[];
  /** Fields this caller may edit; the rest render read-only and are never sent. */
  editable:   readonly OrderLineEditableField[];
  /** react-query keys to invalidate after a successful save / reload. */
  invalidateKeys?: unknown[][];
};

const MIN_REASON = 10;

export function EditOrderLinesModal({
  open, onClose, customerId, orderId, dxVersion: dxVersionProp, lines: linesProp, editable, invalidateKeys = [],
}: Props) {
  const { instance } = useMsal();
  const qc = useQueryClient();
  // ⚠️ PINNED at mount. The snapshot query behind these props can refetch while
  // the modal is open (window focus); following it would send the NEW version
  // with drafts typed against the OLD lines — saving over a change the
  // operator never saw, which is exactly what the lock exists to refuse. A
  // concurrent save is surfaced by the 409 instead. The page mounts this
  // modal fresh on every open, so there is nothing to reset.
  const [{ dxVersion, lines }] = useState(() => ({ dxVersion: dxVersionProp, lines: linesProp }));
  const [drafts, setDrafts] = useState<LineDrafts>(() => initialDrafts(lines));
  const [reason, setReason] = useState('');
  const [conflict, setConflict] = useState<ConflictBody | null>(null);

  const patch = useMemo(() => buildLinesPatch(lines, drafts, editable), [lines, drafts, editable]);
  const totals = useMemo(() => previewTotals(lines, drafts), [lines, drafts]);
  const invalidCells = useMemo(
    () => new Set(patch.invalid.map((c) => `${c.order_line}:${c.field}`)),
    [patch.invalid],
  );

  const update = useMutation<unknown, HttpError, { lines: OrderLinePatch[]; dx_version: number; dx_reason: string }>({
    mutationFn: (body) => apiJson(
      instance,
      `/api/customers/${encodeURIComponent(String(customerId))}/orders/${encodeURIComponent(String(orderId))}/lines`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
    meta: { silent: true }, // 409s are expected; the modal surfaces them
  });

  const invalidateAll = () => invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));

  const close = () => { update.reset(); onClose(); };
  const reloadAndClose = () => { setConflict(null); invalidateAll(); close(); };

  const reasonValid = reason.trim().length >= MIN_REASON;
  const canSave = patch.lines.length > 0 && patch.invalid.length === 0 && reasonValid && !update.isPending;

  const onSave = () => {
    setConflict(null);
    update.mutate(
      { lines: patch.lines, dx_version: dxVersion, dx_reason: reason.trim() },
      {
        onSuccess: () => { invalidateAll(); onClose(); },
        onError: (err) => {
          if (err.status === 409) setConflict((err.body as ConflictBody | undefined) ?? { error: err.message });
        },
      },
    );
  };

  const setCell = (lineNo: number, field: OrderLineEditableField, value: string) =>
    setDrafts((d) => ({ ...d, [lineNo]: { ...d[lineNo]!, [field]: value } }));

  const conflictDiff = useMemo(() => {
    const current = conflict?.current_row?.line_items;
    if (!current) return null;
    const byLine = new Map(current.map((l) => [l.order_line, l]));
    const rows: Array<{ key: string; line: number; item: string; field: string; was: string; now: string }> = [];
    for (const l of lines) {
      const now = byLine.get(l.order_line);
      if (!now) continue;
      for (const f of ORDER_LINE_EDITABLE_FIELDS) {
        if (Number(l[f]) === Number(now[f])) continue;
        rows.push({
          key: `${l.order_line}:${f}`, line: l.order_line, item: l.item_code,
          field: ORDER_LINE_FIELD_LABELS[f], was: String(l[f]), now: String(now[f]),
        });
      }
    }
    return rows;
  }, [conflict, lines]);

  return (
    <Modal
      open={open}
      onClose={close}
      title="Edit order lines"
      size="max-w-5xl"
      footer={
        conflict ? (
          <Button onClick={reloadAndClose}>Reload</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close} disabled={update.isPending}>Cancel</Button>
            <Button onClick={onSave} disabled={!canSave}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </>
        )
      }
    >
      {conflict ? (
        <div className="space-y-3">
          <p className="text-sm text-amber-800">This order changed under you. Your draft can’t be saved.</p>
          {conflictDiff && conflictDiff.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs">
              <p className="mb-2 font-medium text-amber-900">Line values changed by another editor:</p>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-amber-800">
                    <th className="py-1 pr-2">Line</th><th className="py-1 pr-2">Field</th>
                    <th className="py-1 pr-2">Was</th><th className="py-1">Now</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictDiff.map((d) => (
                    <tr key={d.key} className="border-t border-amber-100">
                      <td className="py-1 pr-2 font-mono">{d.line} · {d.item}</td>
                      <td className="py-1 pr-2">{d.field}</td>
                      <td className="py-1 pr-2 text-gray-700">{d.was}</td>
                      <td className="py-1 text-gray-900">{d.now}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-500">Click <strong>Reload</strong> to discard your draft and view the current values.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Each line’s PPV and CV become Qty × Each, and the order’s PPV and CV totals become the sum of its
            lines. Prices, tax and the order total are not changed.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th className="w-32">Item Code</Th>
                  <Th>Description</Th>
                  <Th className="w-24 text-right">{ORDER_LINE_FIELD_LABELS.quantity}</Th>
                  <Th className="w-28 text-right">{ORDER_LINE_FIELD_LABELS.business_volume_each}</Th>
                  <Th className="w-20 text-right">PPV</Th>
                  <Th className="w-28 text-right">{ORDER_LINE_FIELD_LABELS.commissionable_volume_each}</Th>
                  <Th className="w-20 text-right">CV</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((l) => {
                  const draft = drafts[l.order_line];
                  const preview = previewLine(l, draft);
                  const cell = (f: OrderLineEditableField) => (
                    <LineCell
                      field={f}
                      line={l}
                      value={draft?.[f] ?? ''}
                      editable={editable.includes(f)}
                      invalid={invalidCells.has(`${l.order_line}:${f}`)}
                      onChange={(v) => setCell(l.order_line, f, v)}
                    />
                  );
                  return (
                    <tr key={l.order_line}>
                      <Td className="font-mono">{l.item_code}</Td>
                      <Td>{l.item_description}</Td>
                      <Td className="text-right">{cell('quantity')}</Td>
                      <Td className="text-right">{cell('business_volume_each')}</Td>
                      <Td className="text-right tabular-nums">{decimal2(preview.business_volume)}</Td>
                      <Td className="text-right">{cell('commissionable_volume_each')}</Td>
                      <Td className="text-right tabular-nums">{decimal2(preview.commissionable_volume)}</Td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-gray-200">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right text-xs font-medium uppercase text-gray-600">
                    New order totals
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{decimal2(totals.business_volume)}</td>
                  <td />
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{decimal2(totals.commissionable_volume)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Reason for change<RequiredMark /></span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 outline-none focus:border-brand-500"
              rows={2}
              placeholder={`Why is this changing? (min ${MIN_REASON} characters)`}
            />
            {/* Same message, and same timing, as the other edit modals: nothing
                until the operator has typed, then until the reason is long enough. */}
            {reason.length > 0 && !reasonValid && (
              <span className="mt-1 block text-xs text-red-700">reason must be at least {MIN_REASON} characters</span>
            )}
          </label>

          {patch.invalid.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              Every highlighted cell needs a number.
            </div>
          )}
          {update.isError && update.error.status !== 409 && (
            <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">{update.error.message}</div>
          )}
          <p className="text-xs text-gray-500">
            {patch.lines.length === 0
              ? 'No changes yet.'
              : `${patch.lines.length} line${patch.lines.length === 1 ? '' : 's'} changed.`}
            {' '}Saved against <code className="font-mono">dx_version {dxVersion}</code>. On conflict you&apos;ll
            see what changed and can reload.
          </p>
        </div>
      )}
    </Modal>
  );
}

function LineCell({
  field, line, value, editable, invalid, onChange,
}: {
  field:    OrderLineEditableField;
  line:     EditableOrderLine;
  value:    string;
  editable: boolean;
  invalid:  boolean;
  onChange: (v: string) => void;
}) {
  if (!editable) {
    return <span className="tabular-nums text-gray-700" title="read-only">{decimal2(line[field])}</span>;
  }
  const changed = parseDraft(value) !== null && parseDraft(value) !== Number(line[field]);
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={`${ORDER_LINE_FIELD_LABELS[field]}, line ${line.order_line}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={
        'w-full rounded border px-2 py-1 text-right tabular-nums outline-none focus:border-brand-500 ' +
        (invalid ? 'border-red-400 bg-red-50' : changed ? 'border-amber-400 bg-amber-50' : 'border-gray-300')
      }
    />
  );
}
